import { HistoryEntity, ITransaction, IUser, getDashboardLabel } from '@sparling/financial-common'
import { capitalize } from 'lodash'
import { IColumns } from '../../../../../components/Dashboard/Table'

const normalizeInstrument = (value?: string): string | null => {
  if (!value) return null

  const head = value.split('.')[0]
  if (head.length === 6) {
    const base = head.slice(0, 3)
    const quote = head.slice(3, 6)
    return `${base}/${quote}`
  }

  return head
}

export const UnifiedTableColumns: IColumns[] = [
  {
    id: '__type',
    label: 'Type',
    format: (value) => (value ? capitalize(value) : 'N/A'),
  },
  {
    id: 'amount',
    label: 'Amount',
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    format: (...args) => {
      const row = args[4]
      return row?.amount || row?.quantity || 'N/A'
    },
  },
  {
    id: 'asset',
    label: 'Asset',
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    format: (...args) => {
      const row = args[4]
      return row?.asset || normalizeInstrument(row?.instrument) || row?.currency || 'N/A'
    },
  },
  {
    id: 'status',
    label: 'Status',
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    format: (...args) => {
      const row = args[4]
      // eslint-disable-next-line no-underscore-dangle
      if (row.__type === 'TRANSACTION') {
        return getDashboardLabel(row.owner, row.status, row.type) || 'N/A'
      }

      return row.status ? capitalize(row.status) : 'N/A'
    },
  },
  {
    id: 'unifiedHistoryActions',
    label: 'Actions',
  },
]

export const TransactionsTableColumns: IColumns[] = [
  {
    id: 'type',
    label: 'Type',
    format: (value: string) => (value ? capitalize(value) : 'N/A'),
  },
  { id: 'asset', label: 'Asset', format: (value: string) => value || 'N/A' },
  { id: 'amount', label: 'Amount', format: (value: string) => value || 'N/A' },
  { id: 'fee', label: 'Fee', format: (value: string) => value || 'N/A' },
  {
    id: 'status',
    label: 'Status',
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    format: (...args) => {
      const row = args[4]
      const value = getDashboardLabel(row.owner, row.status, row.type)
      return value || 'N/A'
    },
  },
  { id: 'riskScore', label: 'Risk Score', format: (value: string) => value || 'N/A' },
  { id: 'comment', label: 'Comment', format: (value: string) => value || 'N/A' },
  {
    id: 'transactionHistoryActions',
    label: 'Actions',
  },
]

export const TradesTableColumns: IColumns[] = [
  {
    id: 'orderType',
    label: 'Order Type',
    format: (value: string) => value || 'N/A',
  },
  {
    id: 'instrument',
    label: 'Instrument',
    format: (value: string) => (value ? normalizeInstrument(value) : 'N/A'),
  },
  { id: 'quantity', label: 'Quantity', format: (value: string) => value || 'N/A' },
  { id: 'executedPrice', label: 'Executed Price', format: (value: string) => value || 'N/A' },
  {
    id: 'status',
    label: 'Status',
    format: (value: string) => (value ? capitalize(value) : 'N/A'),
  },
  {
    id: 'rejectedReason',
    label: 'Comment',
    format: (value: string) => (value ? `Rejected reason: ${value}` : 'N/A'),
  },
  {
    id: 'actions',
    label: 'Actions',
  },
]

export const CommissionsTableColumns: IColumns[] = [
  {
    id: 'transaction',
    label: 'Commission Type',
    format: (value: ITransaction) => (value?.type ? capitalize(value.type) : 'N/A'),
  },
  { id: 'amount', label: 'Amount', format: (value: string) => value || 'N/A' },
  {
    id: 'status',
    label: 'Status',
    format: (value: string) => (value ? capitalize(value) : 'N/A'),
  },
  {
    id: 'referredUser',
    label: 'Introducee',
    format: (value: IUser) => (value ? `${value.firstName} ${value.lastName}` : 'N/A'),
  },
  {
    id: 'actions',
    label: 'Actions',
  },
]

export const getColumnsByHistoryEntity = (type: HistoryEntity): IColumns[] =>
  ({
    [HistoryEntity.TRANSACTION]: TransactionsTableColumns,
    [HistoryEntity.TRADE]: TradesTableColumns,
    [HistoryEntity.COMMISSION]: CommissionsTableColumns,
    [HistoryEntity.ALL]: UnifiedTableColumns,
  }[type] || UnifiedTableColumns)
